import React, { useContext } from 'react'
import styled from 'styled-components'
import { BlockLink, Button, Flex, Text, Stamp } from 'pcln-design-system'
import { Ac, FreeCancellation, Guests, Luggage, Mileage } from 'pcln-icons'
import { CarListing, RudderProductDesc } from './types'
import { trackCrossSellClicks } from './utils'
import {
  ProductImage,
  ProductFlexBorder,
  ProductTitle,
  SecondaryText,
  PrimaryText,
  ProductWrapper,
  StyledSup
} from './Common'

import BootstrapDataContext from '../../context/BootstrapDataContext'

const CarImage = styled(ProductImage)<{ height: string }>`
  height: ${props => props.height};
  background-repeat: no-repeat;
  background-size: contain;
`

const SmallText = styled(Text)`
  font-size: 10px;
`

const StyledButton = styled(Button)`
  height: 35px;
  font-size: 12px;
`

const StyledStamp = styled(Stamp)`
  border: none;
  border-radius: 2px;
  min-height: 20px;
  width: fit-content;
  font-size: 10px;
`

export default function CarProduct({
  carExample,
  carImageUrl,
  checkoutUrl,
  offerNum,
  rudderParentProductCode,
  partner: { url },
  position,
  pricing: { priceDescription },
  rate: {
    currencySymbol,
    dailyPrice,
    isFreeCancellation,
    savingsPriceTotal,
    totalPrice
  },
  vehicleFeatures: {
    bagCapacity,
    isAirConditioned,
    isUnlimitedMileage,
    peopleCapacity
  }
}: CarListing & {
  position: number
  offerNum: string
  rudderParentProductCode: RudderProductDesc
}) {
  const { webstats, customer } = useContext(BootstrapDataContext)

  const vehicleFeature = (
    Icon: any,
    text: string | number,
    isBold: boolean
  ) => {
    return (
      <Flex alignItems="center">
        <Icon size={14} mr={1} />
        <SmallText fontWeight={isBold ? 'bold' : undefined}>{text}</SmallText>
      </Flex>
    )
  }

  return (
    <ProductWrapper
      data-event-type="cross-sell"
      data-rudder-event
      data-child-product="car"
      data-position={position}
      data-has-inventory
      data-label="Choose"
      data-parent-product={rudderParentProductCode}
      data-source="homepage"
      data-sub-type="cross sell"
      data-cguid={webstats.clientGUID}
      data-email-address={customer.emailAddress}
      data-parent-offer={offerNum}
      className="cross-sell-unit"
    >
      <ProductFlexBorder>
        <Flex flexDirection="column" ml={2} my="5px">
          <CarImage height="58px" bgImage={carImageUrl} />
          <Flex justifyContent="space-between">
            {vehicleFeature(Guests, peopleCapacity, false)}
            {vehicleFeature(Luggage, bagCapacity, false)}
            {isAirConditioned ? vehicleFeature(Ac, 'AC', false) : null}
          </Flex>
        </Flex>
        <Flex flexDirection="column" width="172px" px={2} py={2}>
          <ProductTitle>Economy Car</ProductTitle>
          <SmallText color="text.light" mb={1}>
            {carExample}
          </SmallText>
          {isFreeCancellation
            ? vehicleFeature(FreeCancellation, 'Free Cancellation', true)
            : null}
          {isUnlimitedMileage
            ? vehicleFeature(Mileage, 'Unlimited Mileage', true)
            : null}
        </Flex>
      </ProductFlexBorder>
      <Flex flexDirection="column" px={3} py={2}>
        <Flex justifyContent="space-between" alignItems="flex-start">
          <Flex flexDirection="column">
            <StyledStamp bg="alert">
              <Text color="text.lightest">PAY NOW &amp; SAVE</Text>
            </StyledStamp>
            <SmallText style={{ width: 'fit-content' }} mt={1}>
              Save{' '}
              <strong>
                {currencySymbol}
                {savingsPriceTotal}
              </strong>{' '}
              by paying now
            </SmallText>
          </Flex>
          <Flex alignItems="baseline">
            <Text fontSize={4} color="secondary" fontWeight="light">
              +
            </Text>
            <Text fontSize={4} color="secondary" fontWeight="bold" mr={1}>
              {' '}
              <StyledSup>{currencySymbol}</StyledSup>
              {dailyPrice}
            </Text>
            <PrimaryText color="secondary">{priceDescription}</PrimaryText>
          </Flex>
        </Flex>
        <Flex justifyContent="space-between" alignItems="flex-end">
          <CarImage height="35px" bgImage={url} />
          <Flex flexDirection="column" alignItems="flex-end">
            <SecondaryText color="text.light">
              Total: {currencySymbol}
              {totalPrice}
            </SecondaryText>
            <BlockLink
              onClick={trackCrossSellClicks}
              href={checkoutUrl}
              target="_new"
            >
              <StyledButton color="secondary">Choose</StyledButton>
            </BlockLink>
          </Flex>
        </Flex>
      </Flex>
    </ProductWrapper>
  )
}
